import { memo, useEffect, useState, useCallback } from "react";
import Form from "../Form";
import {
    getOwners,
    getInspectors,
    getMilkRecordings,
    getMilkCalculation,
} from "../../api";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../styles/Select";
import { Box, Typography, Button } from "@mui/material";
import Spinner from "../Spinner.js";
import MilkRecordingForm from "../forms/MilkRecordingForm.js";
import AnimalMilkrecordingForm from "../forms/AnimalMilkrecordingForm.js";
import MilkRecordingMetricsTable from "../tables/MilkRecordingMetricsTable.js";

const OwnerMilkrecordingSelect = ({
    type,
}) => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [fetchedInspectors, setFetchedInspectors] = useState([]);
    const [inspectorsDropdownValues, setInspectorsDropdownValues] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOwnerSelected, setIsOwnerSelected] = useState(false);
    const [isMilkRecordingSelected, setIsMilkRecordingSelected] = useState(false);
    const [fetchedMilkRecordings, setFetchedMilkRecordings] = useState([]);
    const [milkRecordingsDropdownValues, setMilkRecordingsDropdownValues] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedMilkRecording, setSelectedMilkRecording] = useState(null);
    const [milkRecordingInspector, setMilkRecordingInspector] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [fetchedMilkCalculation, setFetchedMilkCalculation] = useState({});

    const classes = useSelectStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners)
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));
                const { inspectors } = await getInspectors();
                setFetchedInspectors(inspectors);
                setInspectorsDropdownValues(inspectors.map((inspector) => ({
                    value: `${inspector?.lastName} ${inspector?.firstName}`,
                    text: `${inspector?.lastName} ${inspector?.firstName}`
                })));
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const selectOwnerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ]

    const selectMilkRecordingFormContent = [
        {
            customType: "dropdown",
            id: "milkRecording",
            items: milkRecordingsDropdownValues,
            placeholder: "Ημ/νία Γαλακτοπαραγωγής",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ]

    const handleOwnerSelect = async (values) => {
        setIsLoading(true);
        const selectedOwnerObj = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner);
        setSelectedOwner(selectedOwnerObj);
        const { success: scs, milkRecordings, message } = await getMilkRecordings(selectedOwnerObj?._id);
        if (scs) {
            setFetchedMilkRecordings(milkRecordings);
            setMilkRecordingsDropdownValues(
                milkRecordings
                    .sort((a, b) => new Date(b.testDate) - new Date(a.testDate))
                    .map((milkRecording) =>
                    ({
                        value: `${new Date(milkRecording.testDate).toLocaleDateString("el-GR")}`,
                        text: `${new Date(milkRecording.testDate).toLocaleDateString("el-GR")}`
                    })
                    )
            );
            setIsOwnerSelected(true);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleMilkRecordingSelect = async (values) => {
        const selectedMilkRecordingObj = fetchedMilkRecordings.find((milkRecording) =>
            new Date(milkRecording.testDate).toLocaleDateString("el-GR") === values.milkRecording
        );
        const { success: scs, milkCalculation, message } = await getMilkCalculation(selectedMilkRecordingObj._id);
        if (scs) {
            setFetchedMilkCalculation(milkCalculation);
        } else {
            error(message);
        }
        setSelectedDate(new Date(selectedMilkRecordingObj.testDate));
        setMilkRecordingInspector(fetchedInspectors.find((inspector) => inspector._id === selectedMilkRecordingObj.inspector))
        setSelectedMilkRecording(selectedMilkRecordingObj);
        setIsMilkRecordingSelected(true);
    }

    const renderComponentByType = () => {
        switch (type) {
            case "SelectMilkrecording":
                return (
                    <MilkRecordingForm
                        isCreate={false}
                        ownersDropdownValues={ownersDropdownValues}
                        selectedOwner={selectedOwner}
                        selectedDate={selectedDate}
                        inspectorsDropdownValues={inspectorsDropdownValues}
                        defaultInspector={milkRecordingInspector}
                        fetchedInspectors={fetchedInspectors}
                        selectedMilkRecording={selectedMilkRecording}
                        setIsOwnerSelected={setIsOwnerSelected}
                        setIsMilkRecordingSelected={setIsMilkRecordingSelected}
                        fetchedMilkCalculation={fetchedMilkCalculation}
                    />
                )
            case "CreateAnimalMilkRecording":
                return (
                    <AnimalMilkrecordingForm
                        setIsMilkRecordingSelected={setIsMilkRecordingSelected}
                        selectedMilkRecording={selectedMilkRecording} />
                )
            case "SelectAnimalMilkRecording":
                return (
                    <AnimalMilkrecordingForm
                        setIsMilkRecordingSelected={setIsMilkRecordingSelected}
                        selectedMilkRecording={selectedMilkRecording}
                        isUpdate={true} />
                )
            case "MilkRecordingMetrics":
                return (
                    <MilkRecordingMetricsTable
                        selectedMilkRecording={selectedMilkRecording} />
                )
        }
    }
    return (
        <>
            <Spinner open={isLoading} />
            {!isOwnerSelected && !isMilkRecordingSelected && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή που σας ενδιαφέρει:
                        </Typography>
                        <Form content={selectOwnerFormContent} onSubmit={handleOwnerSelect} />
                    </Box>
                </Box>
            )}
            {isOwnerSelected && !isMilkRecordingSelected && (
                <>
                    <Box className={classes.outerBox}>
                        <Box container flexDirection="column" className={classes.innerBox}>

                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                Επιλέξτε την Γαλακτοπαραγωγή που σας ενδιαφέρει:
                            </Typography>
                            <Form content={selectMilkRecordingFormContent} onSubmit={handleMilkRecordingSelect} />
                            <Button
                                onClick={() => setIsOwnerSelected(false)}
                                sx={{
                                    marginTop: "1rem",
                                    padding: "0.5rem 1rem",
                                    backgroundColor: "third",
                                    color: "secondary.main",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    variant: "h5",
                                }}
                            >
                                Πίσω
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
            {isOwnerSelected && isMilkRecordingSelected && renderComponentByType()}
        </>
    )
}

export default memo(OwnerMilkrecordingSelect);